import React, { useState, useEffect } from 'react';
import { useProfessoresLazyQuery } from '../graphql/queries/professores.query';
import { useCadastrarProfessorMutation } from '../graphql/mutations/cadastrar_professor.mutation';
import { useDeletarProfessorMutation } from '../graphql/mutations/deletar_professor.mutation';
import { useEditarProfessorMutation } from '../graphql/mutations/editar_professor.mutation';
import { FormProfessor, FormularioProfessor } from '../components/form_professor';
import { FormList } from '../../shared/components/form_components/list';
import { Container } from '../../shared/components/layout_components/container/styles';
import { ProfessoresQuery_professores } from '../graphql/queries/__generated__/ProfessoresQuery';
import { useTitulacoesLazyQuery } from '../graphql/queries/titulacoes.query';
import { TitulacoesQuery_titulacoes } from '../graphql/queries/__generated__/TitulacoesQuery';
import { SubContainer } from '../../shared/components/layout_components/sub_container/styles';

const ProfessorPage: React.FC = () => {
  const [professor, setProfessor] = useState<FormProfessor>();
  const [professores, setProfessores] = useState<ProfessoresQuery_professores[]>([]);
  const [titulacoes, setTitulacoes] = useState<TitulacoesQuery_titulacoes[]>([]);
  const [termoBusca, setTermoBusca] = useState('');
  const [paginaAtual, setPaginaAtual] = useState(1);
  const itens_por_pagina = 25;

  const [obter_professores, { fetchMore, loading }] = useProfessoresLazyQuery();
  const [obter_titulacoes] = useTitulacoesLazyQuery();
  const [cadastar_professor] = useCadastrarProfessorMutation();
  const [editar_professor] = useEditarProfessorMutation();
  const [deletar_professor] = useDeletarProfessorMutation();

  const on_submit = async (professor: FormProfessor) => {
    if (!professor.id_professor) {
      const { data } = await cadastar_professor({
        variables: {
          ...professor,
        },
      });

      if (data) {
        setProfessores([...professores, data.cadastrarProfessor]);
        alert('Professor cadastrado com sucesso!');
      }
    } else {
      const { data } = await editar_professor({
        variables: {
          ...professor,
          id_professor: professor.id_professor,
        },
      });

      if (data) {
        const index = professores.findIndex(
          (professor_atual) => professor_atual.id_professor === data.editarProfessor.id_professor
        );

        if (index !== -1) {
          const professores_atualizados = [...professores];
          professores_atualizados[index] = data.editarProfessor;
          setProfessores(professores_atualizados);
        }

        alert('Professor editado com sucesso!');
      }
    }

    setProfessor(undefined);
  };

  const on_edit = (professor: ProfessoresQuery_professores) => {
    setProfessor({
      id_professor: professor.id_professor || 0,
      dsc_bio: professor.dsc_bio || '',
      dsc_email: professor.dsc_email || '',
      dsc_email2: professor.dsc_email2 || '',
      dsc_email3: professor.dsc_email3 || '',
      dsc_link_facebook: professor.dsc_link_facebook || '',
      dsc_link_instagram: professor.dsc_link_instagram || '',
      dsc_link_lattes: professor.dsc_link_lattes || '',
      dsc_link_linkedin: professor.dsc_link_linkedin || '',
      dsc_link_twitter: professor.dsc_link_twitter || '',
      dsc_nome_professor: professor.dsc_nome_professor || '',
      dsc_num_telefone: professor.dsc_num_telefone || '',
      dsc_num_telefone2: professor.dsc_num_telefone2 || '',
      dsc_titulacao: professor.dsc_titulacao || '',
      flag_casa: Boolean(professor.flag_casa),
    });
  };

  const on_delete = async (id_professor: number) => {
    await deletar_professor({ variables: { id_professor } });
    setProfessores((disciplinas) => disciplinas.filter((d) => d.id_professor !== id_professor));
  };

  const on_cancel_edit = () => {
    setProfessor(undefined);
  };

  const obter_mais_dados = async (descricao = '', pagina = 1) => {
    const { data } = await fetchMore({
      variables: {
        descricao,
        pagina,
        itens_por_pagina,
      },
    });

    if (data && pagina > 1) {
      setProfessores([...professores, ...data.professores]);
    } else {
      setProfessores(data.professores);
    }
  };

  const dados_dados_iniciais = async () => {
    const { data: data_professores } = await obter_professores({
      variables: {
        descricao: '',
        pagina: 1,
        itens_por_pagina,
      },
    });

    const { data: data_titulacoes } = await obter_titulacoes();

    if (data_professores) {
      setProfessores(data_professores.professores);
    }

    if (data_titulacoes) {
      setTitulacoes(data_titulacoes.titulacoes);
    }
  };

  const proxima_pagina = () => {
    setPaginaAtual((paginaAtual) => paginaAtual + 1);
  };

  useEffect(() => {
    obter_mais_dados(termoBusca);
  }, [termoBusca]);

  useEffect(() => {
    obter_mais_dados(termoBusca, paginaAtual);
  }, [paginaAtual]);

  useEffect(() => {
    document.title = 'SisIEC - Professores';
    dados_dados_iniciais();
  }, []);

  return (
    <Container>
      <SubContainer flex={2}>
        <FormularioProfessor
          onSubmit={on_submit}
          onCancel={on_cancel_edit}
          professor={professor}
          titulacoes={titulacoes}
        />
      </SubContainer>

      <SubContainer flex={3}>
        <FormList
          list_name='Professores'
          items={professores.map((p) => ({
            data: p,
            id: p.id_professor,
            label: p.dsc_nome_professor || '',
          }))}
          onSearch={setTermoBusca}
          onScroll={proxima_pagina}
          onEdit={on_edit}
          onDelete={on_delete}
          loading={loading}
        />
      </SubContainer>
    </Container>
  );
};

export { ProfessorPage };
